import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  FileText, Send, Sparkles, CheckCircle2, Circle, PenLine, Table2,
  BarChart3, Target, Users, TrendingUp, DollarSign, Lightbulb,
  ChevronRight, MessageSquare, Plus, Trash2, Save, Download,
  Loader2, Zap, BookOpen, BrainCircuit,
} from "lucide-react";
import { generateBusinessPlanPdf } from "@/utils/generateBusinessPlanPdf";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const bpSections = [
  { id: "executive", title: "Résumé Exécutif", icon: FileText, description: "Vision globale du projet, proposition de valeur, objectifs clés" },
  { id: "market", title: "Étude de Marché", icon: Target, description: "Analyse du marché, concurrence, cible clientèle, tendances" },
  { id: "business_model", title: "Modèle Économique", icon: TrendingUp, description: "Sources de revenus, stratégie de pricing, canaux de distribution" },
  { id: "team", title: "Équipe & Organisation", icon: Users, description: "Fondateurs, compétences clés, organigramme, recrutements prévus" },
  { id: "marketing", title: "Stratégie Marketing", icon: Lightbulb, description: "Positionnement, plan de communication, acquisition clients" },
  { id: "operations", title: "Plan Opérationnel", icon: BarChart3, description: "Processus, fournisseurs, logistique, outils et technologies" },
  { id: "financial", title: "Prévisionnel Financier", icon: DollarSign, description: "Compte de résultat, plan de trésorerie, seuil de rentabilité" },
  { id: "funding", title: "Besoin de Financement", icon: Table2, description: "Investissements, apports, emprunts, levée de fonds" },
];

const defaultFinancialRows = [
  { label: "Chiffre d'affaires", y1: 0, y2: 0, y3: 0 },
  { label: "Coût des marchandises", y1: 0, y2: 0, y3: 0 },
  { label: "Marge brute", y1: 0, y2: 0, y3: 0 },
  { label: "Charges de personnel", y1: 0, y2: 0, y3: 0 },
  { label: "Loyer & charges", y1: 0, y2: 0, y3: 0 },
  { label: "Marketing & communication", y1: 0, y2: 0, y3: 0 },
  { label: "Frais généraux", y1: 0, y2: 0, y3: 0 },
  { label: "Résultat d'exploitation", y1: 0, y2: 0, y3: 0 },
  { label: "Impôts & taxes", y1: 0, y2: 0, y3: 0 },
  { label: "Résultat net", y1: 0, y2: 0, y3: 0 },
];

const defaultEvaluation = [
  { label: "Viabilité du marché", score: 0, color: "text-muted-foreground" },
  { label: "Solidité financière", score: 0, color: "text-muted-foreground" },
  { label: "Différenciation concurrentielle", score: 0, color: "text-muted-foreground" },
  { label: "Qualité de l'équipe", score: 0, color: "text-muted-foreground" },
  { label: "Potentiel de croissance", score: 0, color: "text-muted-foreground" },
  { label: "Risques identifiés", score: 0, color: "text-muted-foreground" },
];

function scoreColor(s: number) {
  if (s >= 75) return "text-green-500";
  if (s >= 60) return "text-yellow-500";
  if (s >= 40) return "text-orange-500";
  return "text-muted-foreground";
}

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_URL}${path}`, { credentials: "include", ...options });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Erreur ${res.status}`);
  }
  return res.json();
}

// ─── Mode Selector ───
function ModeSelector({ mode, onChange }: { mode: string; onChange: (m: string) => void }) {
  const modes = [
    { id: "guided", label: "Mode Guidé", icon: BookOpen, desc: "Remplir section par section avec aide IA" },
    { id: "conversational", label: "Mode Conversationnel", icon: MessageSquare, desc: "Discussion libre, Grafton construit le BP" },
    { id: "express", label: "Mode Express", icon: Zap, desc: "Résumez votre projet → BP complet généré" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="bp-mode-selector">
      {modes.map((m) => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          data-testid={`bp-mode-${m.id}`}
          className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
            mode === m.id
              ? "border-primary bg-primary/5 shadow-md"
              : "border-border/50 hover:border-primary/30 bg-card"
          }`}
        >
          <m.icon className={`h-5 w-5 mt-0.5 shrink-0 ${mode === m.id ? "text-primary" : "text-muted-foreground"}`} />
          <div>
            <div className={`text-sm font-semibold ${mode === m.id ? "text-primary" : "text-foreground"}`}>{m.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{m.desc}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Express Mode ───
function ExpressMode({
  onGenerated,
}: {
  onGenerated: (sections: Record<string, string>, rows: any[], evaluation: any) => void;
}) {
  const [summary, setSummary] = useState("");
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (summary.trim().length < 20) {
      toast.error("Décrivez votre projet en au moins 20 caractères");
      return;
    }
    setGenerating(true);
    try {
      const data = await apiFetch("/api/business-plan/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ summary }),
      });
      onGenerated(data.sections, data.financial_rows || [], data.evaluation || {});
      toast.success("Business Plan généré avec succès !");
    } catch (err: any) {
      toast.error(err.message || "Erreur de génération");
    }
    setGenerating(false);
  };

  return (
    <Card className="border-primary/20" data-testid="bp-express-mode">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Génération Express
        </CardTitle>
        <CardDescription>
          Décrivez votre projet en quelques lignes et Grafton IA génère l'intégralité du business plan.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          placeholder="Exemple : Je souhaite ouvrir un restaurant gastronomique écoresponsable à Lyon, ciblant une clientèle CSP+ de 30-55 ans. Budget initial de 250 000€, ouverture prévue en septembre 2026. L'originalité : des menus qui changent chaque semaine en fonction des producteurs locaux, avec une expérience immersive en cuisine ouverte..."
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={8}
          className="resize-none text-sm"
          data-testid="bp-express-summary"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{summary.length} caractères</span>
          <Button
            onClick={handleGenerate}
            disabled={generating || summary.trim().length < 20}
            className="gap-2"
            data-testid="bp-express-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Grafton génère votre BP...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Générer le Business Plan
              </>
            )}
          </Button>
        </div>
        {generating && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-center">
            <BrainCircuit className="h-8 w-8 text-primary mx-auto mb-2 animate-pulse" />
            <p className="text-sm text-muted-foreground">
              Grafton IA analyse votre projet et rédige les 8 sections du business plan...
            </p>
            <p className="text-xs text-muted-foreground mt-1">Cela peut prendre 20-30 secondes</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Conversational Mode ───
function ConversationalMode({
  sections,
  onSectionsUpdate,
}: {
  sections: Record<string, string>;
  onSectionsUpdate: (s: Record<string, string>) => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "Bonjour ! Je suis Grafton IA, votre assistant business plan. Parlez-moi de votre projet : quelle est votre idée d'entreprise ? Je vais vous guider étape par étape pour construire un BP solide.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg: ChatMessage = { role: "user", content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const bpContext = bpSections
        .map((s) => `### ${s.title}\n${sections[s.id] || "(non rempli)"}`)
        .join("\n\n");

      const data = await apiFetch("/api/business-plan/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: input, context: bpContext }),
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Erreur de connexion. Réessayez." }]);
    }
    setLoading(false);
  };

  return (
    <Card className="border-primary/20" data-testid="bp-conversational-mode">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-5 w-5 text-primary" />
          Discussion Business Plan
        </CardTitle>
        <CardDescription>Grafton vous guide pour construire votre BP par la conversation</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[450px] overflow-y-auto space-y-3 mb-3 p-3 bg-muted/30 rounded-lg" data-testid="bp-conv-chat">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border text-white"
                }`}
              >
                {m.role === "assistant" ? (
                  <div className="prose prose-sm prose-invert prose-p:text-white prose-li:text-white prose-headings:text-white prose-strong:text-primary max-w-none">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                ) : (
                  m.content
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-card border rounded-xl px-4 py-2.5 text-sm text-muted-foreground animate-pulse">
                Grafton réfléchit...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Décrivez votre projet, posez vos questions..."
            disabled={loading}
            data-testid="bp-conv-input"
          />
          <Button size="icon" onClick={sendMessage} disabled={loading || !input.trim()} data-testid="bp-conv-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {[
            "Aide-moi à définir mon marché cible",
            "Comment structurer mon modèle économique ?",
            "Quels sont mes besoins de financement ?",
          ].map((q) => (
            <button
              key={q}
              onClick={() => setInput(q)}
              className="text-xs px-2.5 py-1.5 rounded-full bg-muted/50 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              {q}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Component ───
export default function BusinessPlanDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("guided");
  const [sections, setSections] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState("executive");
  const [financialRows, setFinancialRows] = useState(defaultFinancialRows);
  const [evaluation, setEvaluation] = useState(defaultEvaluation);
  const [saving, setSaving] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Chat (guided mode sidebar)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [generatingSection, setGeneratingSection] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Load BP from MongoDB
  const loadBP = useCallback(async () => {
    try {
      const data = await apiFetch("/api/business-plan");
      if (data.sections && Object.keys(data.sections).length > 0) {
        setSections(data.sections);
      }
      if (data.financial_rows && data.financial_rows.length > 0) {
        setFinancialRows(data.financial_rows);
      }
      if (data.mode) setMode(data.mode);
      setDataLoaded(true);
    } catch {
      setDataLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user) loadBP();
  }, [user, loadBP]);

  // Save BP to MongoDB
  const saveBP = useCallback(async () => {
    setSaving(true);
    try {
      await apiFetch("/api/business-plan", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sections, financial_rows: financialRows, mode }),
      });
      toast.success("Business Plan sauvegardé");
    } catch {
      toast.error("Erreur de sauvegarde");
    }
    setSaving(false);
  }, [sections, financialRows, mode]);

  const handleSectionChange = (id: string, value: string) => {
    setSections((prev) => ({ ...prev, [id]: value }));
  };

  const handleFinancialChange = (index: number, field: "y1" | "y2" | "y3", value: string) => {
    setFinancialRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
      return updated;
    });
  };

  const addFinancialRow = () => {
    setFinancialRows((prev) => [...prev, { label: "Nouvelle ligne", y1: 0, y2: 0, y3: 0 }]);
  };

  const removeFinancialRow = (index: number) => {
    setFinancialRows((prev) => prev.filter((_, i) => i !== index));
  };

  // AI generate a single section
  const generateSection = async (sectionId: string) => {
    const section = bpSections.find((s) => s.id === sectionId);
    if (!section) return;
    setGeneratingSection(sectionId);
    try {
      const data = await apiFetch("/api/business-plan/generate-section", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          section_id: sectionId,
          section_title: section.title,
          project_summary: sections.executive || "",
          existing_sections: sections,
        }),
      });
      setSections((prev) => ({ ...prev, [sectionId]: data.content }));
      toast.success(`Section "${section.title}" générée`);
    } catch (err: any) {
      toast.error(err.message || "Erreur de génération");
    }
    setGeneratingSection(null);
  };

  // Chat (guided mode)
  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg: ChatMessage = { role: "user", content: chatInput };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    setChatLoading(true);
    try {
      const bpContext = bpSections
        .map((s) => `### ${s.title}\n${sections[s.id] || "(non rempli)"}`)
        .join("\n\n");
      const data = await apiFetch("/api/business-plan/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: chatInput, context: bpContext }),
      });
      setChatMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
    } catch {
      setChatMessages((prev) => [...prev, { role: "assistant", content: "Erreur. Réessayez." }]);
    }
    setChatLoading(false);
  };

  // Express mode callback
  const handleExpressGenerated = (newSections: Record<string, string>, rows: any[], eval_data: any) => {
    setSections(newSections);
    if (rows.length > 0) setFinancialRows(rows);
    if (eval_data) {
      const map: Record<string, string> = {
        viabilite_marche: "Viabilité du marché",
        solidite_financiere: "Solidité financière",
        differenciation: "Différenciation concurrentielle",
        qualite_equipe: "Qualité de l'équipe",
        potentiel_croissance: "Potentiel de croissance",
        risques: "Risques identifiés",
      };
      const newEval = Object.entries(map).map(([key, label]) => {
        const s = eval_data[key] || 0;
        return { label, score: s, color: scoreColor(s) };
      });
      setEvaluation(newEval);
    }
    setMode("guided"); // Switch to guided mode to review
  };

  // PDF generation
  const handlePdf = () => {
    setPdfGenerating(true);
    setTimeout(() => {
      const blob = generateBusinessPlanPdf(sections, bpSections, financialRows, evaluation);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "business-plan-grafton.pdf";
      a.click();
      URL.revokeObjectURL(url);
      setPdfGenerating(false);
      toast.success("PDF téléchargé");
    }, 100);
  };

  if (authLoading || !dataLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return null;

  const completionPct = Math.round(
    (bpSections.filter((s) => (sections[s.id] || "").trim().length > 20).length / bpSections.length) * 100
  );
  const currentSection = bpSections.find((s) => s.id === activeSection)!;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="h-14 border-b border-border flex items-center px-4 gap-3 bg-card">
            <SidebarTrigger />
            <FileText className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold text-foreground">Business Plan</h1>
            <Badge variant="outline" className="text-xs" data-testid="bp-completion">
              {completionPct}% complété
            </Badge>
            <div className="flex-1" />
            <Button variant="outline" size="sm" className="gap-1.5" onClick={saveBP} disabled={saving} data-testid="bp-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePdf} disabled={pdfGenerating} data-testid="bp-pdf-btn">
              {pdfGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Générer PDF
            </Button>
          </header>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Mode selector */}
            <ModeSelector mode={mode} onChange={setMode} />

            {/* ═══ EXPRESS MODE ═══ */}
            {mode === "express" && <ExpressMode onGenerated={handleExpressGenerated} />}

            {/* ═══ CONVERSATIONAL MODE ═══ */}
            {mode === "conversational" && (
              <ConversationalMode sections={sections} onSectionsUpdate={setSections} />
            )}

            {/* ═══ GUIDED MODE ═══ */}
            {mode === "guided" && (
              <div className="flex gap-6">
                {/* Main content */}
                <div className="flex-1 space-y-6">
                  <Tabs defaultValue="redaction" className="space-y-6">
                    <TabsList className="grid grid-cols-3 w-full max-w-lg">
                      <TabsTrigger value="redaction" className="gap-2" data-testid="bp-tab-redaction">
                        <PenLine className="h-4 w-4" /> Rédaction
                      </TabsTrigger>
                      <TabsTrigger value="financial" className="gap-2" data-testid="bp-tab-financial">
                        <Table2 className="h-4 w-4" /> Financier
                      </TabsTrigger>
                      <TabsTrigger value="evaluation" className="gap-2" data-testid="bp-tab-evaluation">
                        <BarChart3 className="h-4 w-4" /> Évaluation
                      </TabsTrigger>
                    </TabsList>

                    {/* Rédaction */}
                    <TabsContent value="redaction" className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        <div className="space-y-2">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Sections du BP</h3>
                          {bpSections.map((s) => {
                            const filled = (sections[s.id] || "").trim().length > 20;
                            return (
                              <button
                                key={s.id}
                                onClick={() => setActiveSection(s.id)}
                                data-testid={`bp-section-${s.id}`}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
                                  activeSection === s.id
                                    ? "bg-primary/10 text-primary font-medium"
                                    : "hover:bg-muted/50 text-muted-foreground"
                                }`}
                              >
                                {filled ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" /> : <Circle className="h-4 w-4 shrink-0" />}
                                <span className="truncate">{s.title}</span>
                              </button>
                            );
                          })}
                          <div className="pt-4">
                            <div className="text-xs text-muted-foreground mb-1">Progression</div>
                            <Progress value={completionPct} className="h-2" />
                            <div className="text-xs text-muted-foreground mt-1">{completionPct}%</div>
                          </div>
                        </div>

                        <div className="lg:col-span-3">
                          <Card>
                            <CardHeader>
                              <div className="flex items-center gap-3">
                                <currentSection.icon className="h-5 w-5 text-primary" />
                                <div className="flex-1">
                                  <CardTitle className="text-lg">{currentSection.title}</CardTitle>
                                  <CardDescription>{currentSection.description}</CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <Textarea
                                placeholder={`Rédigez la section "${currentSection.title}"...\n\nUtilisez le bouton "Générer avec IA" ou le chat pour obtenir de l'aide.`}
                                value={sections[activeSection] || ""}
                                onChange={(e) => handleSectionChange(activeSection, e.target.value)}
                                className="min-h-[300px] text-sm leading-relaxed"
                                data-testid="bp-section-textarea"
                              />
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1.5"
                                  onClick={() => generateSection(activeSection)}
                                  disabled={generatingSection === activeSection}
                                  data-testid="bp-generate-section-btn"
                                >
                                  {generatingSection === activeSection ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-4 w-4" />
                                  )}
                                  {generatingSection === activeSection ? "Génération..." : "Générer avec IA"}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setChatInput(`Aide-moi avec la section "${currentSection.title}" : ${currentSection.description}`)}
                                >
                                  <MessageSquare className="h-4 w-4 mr-1" /> Demander à Grafton
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Financier */}
                    <TabsContent value="financial" className="space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Table2 className="h-5 w-5 text-primary" />
                            Prévisionnel Financier sur 3 ans
                          </CardTitle>
                          <CardDescription>Modifiez les cellules directement.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="bp-financial-table">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="text-left py-3 px-2 text-muted-foreground font-medium w-1/3">Poste</th>
                                  <th className="text-right py-3 px-2 text-muted-foreground font-medium">Année 1</th>
                                  <th className="text-right py-3 px-2 text-muted-foreground font-medium">Année 2</th>
                                  <th className="text-right py-3 px-2 text-muted-foreground font-medium">Année 3</th>
                                  <th className="w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {financialRows.map((row, i) => {
                                  const isResult = row.label.includes("Résultat") || row.label.includes("Marge");
                                  return (
                                    <tr key={i} className={`border-b border-border/50 ${isResult ? "bg-muted/30 font-semibold" : ""}`}>
                                      <td className="py-2 px-2">
                                        <Input
                                          value={row.label}
                                          onChange={(e) => {
                                            setFinancialRows((prev) => {
                                              const u = [...prev];
                                              u[i] = { ...u[i], label: e.target.value };
                                              return u;
                                            });
                                          }}
                                          className="border-0 bg-transparent h-8 text-sm p-0"
                                        />
                                      </td>
                                      {(["y1", "y2", "y3"] as const).map((f) => (
                                        <td key={f} className="py-2 px-2 text-right">
                                          <Input
                                            type="number"
                                            value={row[f]}
                                            onChange={(e) => handleFinancialChange(i, f, e.target.value)}
                                            className={`border-0 bg-transparent h-8 text-sm text-right p-0 ${row[f] < 0 ? "text-destructive" : ""}`}
                                          />
                                        </td>
                                      ))}
                                      <td className="py-2 px-1">
                                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFinancialRow(i)}>
                                          <Trash2 className="h-3 w-3 text-muted-foreground" />
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={addFinancialRow} data-testid="bp-add-row">
                              <Plus className="h-4 w-4 mr-1" /> Ajouter une ligne
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Évaluation */}
                    <TabsContent value="evaluation" className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <BarChart3 className="h-5 w-5 text-primary" />
                              Scoring du Projet
                            </CardTitle>
                            <CardDescription>Évaluation par Grafton IA</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4" data-testid="bp-evaluation">
                            {evaluation.map((c) => (
                              <div key={c.label} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-foreground">{c.label}</span>
                                  <span className={`font-semibold ${c.color}`}>{c.score}/100</span>
                                </div>
                                <Progress value={c.score} className="h-2" />
                              </div>
                            ))}
                            <div className="pt-4 border-t border-border">
                              <div className="flex justify-between items-center">
                                <span className="font-semibold">Score global</span>
                                <span className="text-2xl font-bold text-primary">
                                  {evaluation.some((e) => e.score > 0)
                                    ? `${Math.round(evaluation.reduce((a, c) => a + c.score, 0) / evaluation.length)}/100`
                                    : "—"}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Lightbulb className="h-5 w-5 text-primary" />
                              Comment améliorer le score
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {completionPct < 100 ? (
                              <div className="flex items-start gap-2 p-3 rounded-lg text-sm bg-amber-500/10 text-amber-700 dark:text-amber-400">
                                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>Complétez les {bpSections.filter((s) => !(sections[s.id] || "").trim()).length} sections manquantes pour obtenir un scoring complet.</span>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2 p-3 rounded-lg text-sm bg-green-500/10 text-green-700 dark:text-green-400">
                                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>Toutes les sections sont remplies. Utilisez le mode Express pour re-générer et améliorer.</span>
                              </div>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() => {
                                setMode("express");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                            >
                              <Zap className="h-4 w-4 mr-1" /> Régénérer avec l'IA
                            </Button>
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Chat sidebar (guided mode) */}
                <div className="w-72 shrink-0 hidden lg:flex flex-col border border-border rounded-xl bg-card overflow-hidden">
                  <div className="p-3 border-b border-border flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Assistant BP</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {chatMessages.length === 0 && (
                      <div className="text-center text-muted-foreground text-xs py-8 space-y-2">
                        <Sparkles className="h-8 w-8 mx-auto text-primary/40" />
                        <p>Posez vos questions pour rédiger votre BP.</p>
                        <div className="space-y-1 pt-2">
                          {["Comment structurer mon résumé exécutif ?", "Quels ratios financiers sont importants ?", "Comment évaluer la taille de mon marché ?"].map((q) => (
                            <button key={q} onClick={() => setChatInput(q)} className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {chatMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[90%] rounded-xl px-3 py-2 text-xs ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-white"}`}>
                          {m.role === "assistant" ? (
                            <div className="prose prose-xs prose-invert prose-p:text-white prose-li:text-white prose-headings:text-white max-w-none"><ReactMarkdown>{m.content || "..."}</ReactMarkdown></div>
                          ) : m.content}
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="p-3 border-t border-border">
                    <form onSubmit={(e) => { e.preventDefault(); sendChat(); }} className="flex gap-2">
                      <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Votre question..." className="text-xs h-8" disabled={chatLoading} data-testid="bp-chat-input" />
                      <Button size="icon" className="h-8 w-8 shrink-0" disabled={chatLoading || !chatInput.trim()} data-testid="bp-chat-send">
                        <Send className="h-3 w-3" />
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
